import { Tooltip } from '@/components/ui/tooltip';
import {
  ADMIN_RAIL,
  ADMIN_RAIL_SETTINGS,
  type AdminRailEntry,
  type AdminRailIcon,
  activeRailId,
} from '@/lib/admin-nav';
import { useAdminSession, useAdminSignOut } from '@/lib/admin-session';
import { useLinkProps, useRouterState } from '@tanstack/react-router';
import {
  ClipboardList,
  FlaskConical,
  Images,
  ListChecks,
  LogOut,
  MessagesSquare,
  NotebookText,
  Settings,
  Theater,
} from 'lucide-react';
import type { AnchorHTMLAttributes, ComponentType, Ref } from 'react';

/**
 * The rail that ties the administration screens together (issue #24).
 *
 * It replaces the horizontal tab bar of BUILD-851, whose three defects
 * compounded: eight tabs needed ~805 px and scrolled sideways below that with a
 * 32 px gradient as the only hint; the bar cost two lines at the top of every
 * screen; and one of its entries had no fixed target.
 *
 * The model is `words-interface`'s `word-rail.tsx`, followed to the pixel -
 * 40 px items, a 10 px radius, a `gray-100` ground, `gray-200` on hover,
 * `gray-50` and `bleu-nuit` when active. The one thing that could NOT be
 * carried across is the active fill: that rail draws Material Symbols and
 * switches their `FILL` variable-font axis to 1. We draw `lucide` SVG, which
 * has no fill axis, so the active entry thickens its stroke instead. The tint
 * and the `bleu-nuit` already carry the state; the stroke is what keeps the
 * icon from looking merely tinted.
 *
 * Colour stays at level 2 (black, white, `bleu-nuit`): the noyau marks the
 * active entry and does nothing else. `aria-current="page"` carries the same
 * information without relying on colour - which matters more here than it did
 * on the bar, since an icon has no label to read.
 *
 * ## Below 768 px it becomes a bottom tab bar, and that is not cosmetic
 *
 * `admin-responsive.spec.ts` asserts that seventeen administration paths do not
 * scroll the body sideways at 390 px, and the four causes it has already closed
 * were margins of 12 to 20 px - a `<fieldset>`'s `min-inline-size`, a grid child
 * at `min-width: auto`, a `whitespace-nowrap` control, a `<textarea>`'s `cols`
 * contribution crossing an auto track. A 56 px rail would have taken 14 % of a
 * phone's width away from all seventeen at once. Folding to the bottom keeps
 * the content column exactly as wide as it is today.
 *
 * Sign-out is DESKTOP-ONLY, for the reason `words-interface` makes it so: the
 * eight destinations - Médiathèque included, since issue #121 - fill a 390 px
 * bar on their own, and a NINTH item would be the one that overflows it. On a
 * phone the way out lives in Réglages, the screen that already holds what
 * applies to the whole dispositif.
 *
 * ## Why this rail builds its anchors instead of rendering `<Link>`
 *
 * `Link` decides `aria-current` itself, and it decides it LAST - `link.js`
 * spreads `isActive && { 'data-status': 'active', 'aria-current': 'page' }`
 * after everything a caller passes, so no prop can suppress it. Its notion of
 * active is « the path is a prefix of the location », and this rail has entries
 * that notion gets wrong: `/admin` is a prefix of every screen there is, and
 * « Réglages » is a prefix of its own sub-screens.
 *
 * So `activeRailId` decides, once, in a pure function that is unit-tested; the
 * router still owns navigation through `useLinkProps` (href, click handling,
 * preloading); and the two attributes it forces are dropped on the way out. The
 * defect this replaces was real and silent - the root and the current screen
 * both marked, green through every assertion, caught on the MR screenshots.
 */

/**
 * The drawings, resolved from the catalogue's names.
 *
 * `lucide-react` and not a hand-drawn set: the rule `block-icon.tsx` states is
 * « inline SVG on `currentColor` and never an emoji or an icon font », and
 * lucide renders exactly that. It has been a dependency since the repo was
 * generated and had no importer until this file - which is also why the app
 * still loads no icon webfont, and `index.html` self-hosts Titillium with no
 * request to a font CDN at all.
 */
const ICONS: Record<AdminRailIcon, ComponentType<{ size?: number; strokeWidth?: number }>> = {
  sessions: NotebookText,
  interviews: MessagesSquare,
  experiences: FlaskConical,
  protocol: ClipboardList,
  criteria: ListChecks,
  // The masks of a persona: a voice put on, which is what §10 makes the object.
  // `User` would have named a person, and a persona is not one.
  personas: Theater,
  // Plural, and deliberately: a stack of pictures reads as a library of files
  // rather than as one image, which is the one thing this entry must not be
  // mistaken for (issue #121).
  mediatheque: Images,
  settings: Settings,
};

/**
 * ONE class string chosen by a ternary, and NOT `activeProps.className`.
 *
 * TanStack CONCATENATES `className` and `activeProps.className`, so an active
 * anchor used to carry the base utility AND its override at equal specificity:
 * the winner was decided by their order in the generated stylesheet, not by
 * their order in the attribute. Both the unit tests and the e2e assertions
 * stayed green; it was caught on the MR screenshots (2026-08-07). A single
 * string cannot collide with itself.
 */
/*
 * Everything an item wears EXCEPT its `display`, which each caller states for
 * itself - and that omission is the point.
 *
 * `inline-flex` used to live here, so the sign-out carried `inline-flex` AND
 * `hidden`: same specificity, same attribute, winner decided by the order of
 * the generated stylesheet. `inline-flex` won, and the button that S2 makes
 * desktop-only was drawn in the phone's bottom bar - a seventh entry squeezing
 * the six that are supposed to fit at 65 px each.
 *
 * Third time in this file, counting the two the header recounts. The rule that
 * comes out of it: a shared base string may not set a property any caller
 * overrides. `display` is one, background is another - see `ITEM_OFF`.
 */
const ITEM_BASE =
  'relative h-10 w-10 shrink-0 items-center justify-center rounded-rail border-0 p-0 no-underline transition-colors';
/** The links, which are always drawn. */
const ITEM_SHOWN = 'inline-flex';
/*
 * `bg-transparent` lives HERE and not in `ITEM_BASE`, and that placement is the
 * whole point of the split.
 *
 * It was in the base at first, so an active item carried `bg-transparent` AND
 * `bg-gray-50` - two utilities of equal specificity, in one class attribute,
 * whose winner is decided by their order in the generated stylesheet rather
 * than by their order in the string. Tailwind emits `bg-transparent` after
 * `bg-gray-50`, so the active entry painted nothing at all while
 * `aria-current` stayed perfectly correct.
 *
 * That is the SAME defect the header of this file recounts against
 * `activeProps.className`, reintroduced by hand three lines below the paragraph
 * warning about it - and caught the same way it was caught in 2026-08-07, by
 * the e2e assertion on the COMPUTED style rather than by any unit test. The two
 * strings must stay mutually exclusive: whatever one sets, the other overrides.
 */
const ITEM_OFF = 'bg-transparent text-gray-500 hover:bg-gray-200 hover:text-noir';
const ITEM_ON = 'bg-gray-50 text-bleu-nuit';

export function AdminRail() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const active = activeRailId(pathname);
  const { signOut, signingOut } = useAdminSignOut();
  /*
   * `null` is a machine and not an absence to hide (T6/T8): the caller came in
   * by the shared token, so there is no session to close and no button to show.
   * The bar made the same distinction before the rail did.
   */
  const { email } = useAdminSession();

  return (
    /*
     * `aria-label="Administration"` is INHERITED from the tab bar, deliberately.
     * Ten e2e assertions wait on `nav[aria-label="Administration"]`, including
     * every `beforeEach` of `admin-responsive.spec.ts`: the furniture changed,
     * what it is called did not. Renaming it here would have cost a rewrite of
     * assertions that are still testing exactly what they were testing.
     *
     * `z-20`, deliberately BELOW the full-screen overlays: the tester preview
     * (`z-30`, its chrome `z-40`), the media picker and the modals (`z-40`), the
     * narrow session sheet (`z-50`). The same ceiling the bar of BUILD-857 was
     * held to, and for the same reason - a rail painted over the night station
     * is the rupture I8 forbids.
     *
     * Two layouts in one element rather than two components: a phone and a
     * desktop serve the SAME six destinations in the same order, and a second
     * component would be a second place for that order to drift.
     */
    <nav
      aria-label="Administration"
      className={[
        /*
          Phone: a fixed bar along the bottom edge. `h-admin-rail` is the SAME
          token that gives the column its width - the rail is 56 px thick
          whichever way it lies, and `scroll-padding-bottom` in `styles.css`
          makes room for exactly this value.
        */
        'fixed inset-x-0 bottom-0 z-20 flex h-admin-rail flex-row items-center justify-around',
        'border-gray-200 border-t bg-gray-100',
        // Desktop: a sticky column along the left edge. `self-start` with an
        // explicit height is what lets it stick - a stretched flex item is
        // already as tall as its container and would have nothing to stick to.
        'md:sticky md:inset-x-auto md:top-0 md:h-screen md:w-admin-rail md:flex-col',
        'md:justify-start md:gap-1 md:self-start md:border-t-0 md:border-r md:py-3',
      ].join(' ')}
    >
      {ADMIN_RAIL.map((entry) => (
        <RailLink key={entry.id} entry={entry} on={active === entry.id} />
      ))}

      {/*
        The spacer is what pushes Réglages and the sign-out to the bottom edge,
        and it exists only on the rail: the phone bar spreads its six entries
        with `justify-around` instead, because a bottom bar has no bottom.
      */}
      <div aria-hidden="true" className="hidden md:block md:flex-1" />

      {ADMIN_RAIL_SETTINGS.map((entry) => (
        <RailLink key={entry.id} entry={entry} on={active === entry.id} />
      ))}

      {email !== null && (
        /* `side="right"`: see `RailLink`. This one is desktop-only, so it is
           only ever drawn on the left-hand column. */
        <Tooltip label="Se déconnecter" side="right">
          <button
            type="button"
            onClick={signOut}
            disabled={signingOut}
            /*
              `hidden md:inline-flex` is the desktop-only rule of the header,
              spelled on the element that carries it. On a phone the way out is
              in Réglages - see `reglages.tsx`, which mounts it there and says so.
            */
            className={`${ITEM_BASE} ${ITEM_OFF} hidden disabled:text-gray-300 md:inline-flex`}
            aria-label="Se déconnecter"
          >
            <LogOut size={22} strokeWidth={2} />
          </button>
        </Tooltip>
      )}
    </nav>
  );
}

/**
 * What `useLinkProps` hands back, named so the attributes this rail drops can
 * be destructured out. `data-status` is not part of React's anchor typing - it
 * is the router's own marker - so it is spelled here rather than cast away.
 */
type RouterAnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  // `| undefined` spelled out: `exactOptionalPropertyTypes` is on, and what the
  // router hands back declares these as optional-and-possibly-undefined.
  ref?: Ref<HTMLAnchorElement> | undefined;
  'data-status'?: string | undefined;
};

function RailLink({ entry, on }: { entry: AdminRailEntry; on: boolean }) {
  const link = useLinkProps({ to: entry.to }) as RouterAnchorProps;
  const {
    'aria-current': _routerCurrent,
    'data-status': _routerStatus,
    className: _routerClassName,
    ...rest
  } = link;
  const Icon = ICONS[entry.icon];
  return (
    /*
      `side="right"` rather than the default `top`, and it is the one place in
      the migration of #103 where the default is wrong. In desktop this rail is
      a COLUMN glued to the left edge: a bubble above an entry lands on the
      entry before it, which is the neighbour it does not name. Right is the
      side the page itself opens towards, so the bubble points at the content
      it is about. Below 768 px the rail folds into a bottom bar and the
      component's own flip brings the bubble back inside the viewport.
    */
    <Tooltip label={entry.label} side="right">
      <a
        {...rest}
        className={`${ITEM_BASE} ${ITEM_SHOWN} ${on ? ITEM_ON : ITEM_OFF}`}
        /*
          The label is never painted, so `aria-label` is the only place the
          entry is named - it is the accessible name the e2e suite selects by,
          `admin-shell.spec.ts` clicks « Expériences » through it - and the
          `Tooltip` above says the same word to a sighted operator on hover.

          That bubble is DRAWN and no longer the native `title` (issue #103):
          the browser paints a `title` itself, so on a machine set to a dark
          theme it comes out white on black and no CSS of this repo can catch
          it. The bubble carries no `aria-describedby` here on purpose - its
          text IS the accessible name, and a screen reader would say the word
          twice.
        */
        aria-label={entry.label}
        // The one signal that does not rely on colour, and the only place it is
        // set. See the header for why the router cannot be trusted with it here.
        aria-current={on ? 'page' : undefined}
        data-status={on ? 'active' : undefined}
      >
        {/*
          `strokeWidth` 2.5 where lucide's default is 2: the substitute for the
          `FILL 1` axis of the reference rail, which lucide has no equivalent
          for. Named in the header rather than left as a magic number.
        */}
        <Icon size={22} strokeWidth={on ? 2.5 : 2} />
      </a>
    </Tooltip>
  );
}
